import {
  ITEM_PATH,
  ITEM_EDIT_PATH,
  ACCOUNT_SETUP_PATH,
} from 'constants/general';
import {
  CheckBox,
  TextInput,
  PhotoInput,
  SelectInput,
} from 'components/Inputs/';
import { history } from 'store/store';
import { capitalizeFirstLetter } from 'utils/';
import {
  StyledButton,
  StyledCaption,
  QuantityButton,
  QuantityWrapper,
  StyledErrorLabel,
  StyledSubHeading,
} from 'components/Item/General/General.styled';
import { FlexCol, FlexRow } from 'components/Layout/';
import { ReactComponent as AddIcon } from 'icons/add.svg';
import DatePicker from 'components/DatePicker/DatePicker';
import React, { useMemo, useState, useEffect } from 'react';
import { ReactComponent as MinusIcon } from 'icons/minus.svg';
import { SALE, LEASE, DURABLE, PERISHABLE } from 'constants/inventory';

function General({
  id,
  item,
  state,
  errors,
  setState,
  ordering,
  itemLogo,
  attributes,
  categories,
  warehouses,
  isReadOnly,
  setItemLogo,
  warehouseId,
  maxQuantity,
  showDetails,
  isOnBoarding,
  itemCategory,
  hasCondition,
  onDateChange,
  quantityUnits,
  onInputChange,
  setShowDetails,
  onShowQuantityModal,
  currentQuantityUnit,
}) {
  const hasItemId = !!id;
  const isForLease = state.itemOrderType === LEASE;
  const isPerishable = state.itemType === PERISHABLE;
  const [categoryOption, setCategoryOption] = useState(null);
  const [warehouseOption, setWarehouseOption] = useState(null);
  const [unitOption, setUnitOption] = useState(currentQuantityUnit);

  /**
   * @param {{
   * label: string
   * value: string
   * }} option
   */
  function onQuantityUnitChange(option) {
    let quantityUnit = null;
    setUnitOption(option);
    if (option) {
      const { value } = option;
      quantityUnit = value;
    }
    setState({
      ...state,
      quantityUnit,
    });
  }

  /**
   * @param {{
   * label: string
   * value: string
   * }} option
   */
  function onWarehouseChange(option) {
    let warehouse = null;
    setWarehouseOption(option);
    if (option) {
      const { value } = option;
      warehouse = value;
    }
    setState({
      ...state,
      warehouse,
    });
  }

  /**
   * @param {{
   * label: string
   * value: string
   * }} option
   */
  function onCategoryChange(option) {
    let category = null;
    setCategoryOption(option);
    if (option) {
      const { value } = option;
      category = value;
    }
    setState({
      ...state,
      category,
    });
  }

  const attributeCount = useMemo(() => {
    if (attributes && attributes.length) {
      const uniqueNames = new Set();
      attributes.forEach(function (attribute) {
        uniqueNames.add(attribute.attribute_name);
      });
      return uniqueNames.size;
    }
    return 0;
  }, [attributes]);

  const warehouseOptions = useMemo(() => {
    return [...warehouses].map(function ({ id, name }) {
      return { value: id, label: name };
    });
  }, [warehouses]);

  const categoryOptions = useMemo(() => {
    return [...categories].map(function ({ id, category }) {
      return {
        value: id,
        label: capitalizeFirstLetter(category),
      };
    });
  }, [categories]);

  const unitsOptions = useMemo(() => {
    return quantityUnits?.map(function (unit) {
      return {
        value: unit,
        label: unit,
      };
    });
  }, [quantityUnits]);

  /**
   * @param {string} value
   */
  function onItemTypeCheckChange(value) {
    const itemType = state.itemType === value ? '' : value;
    setState({
      ...state,
      itemType,
    });
  }

  /**
   * @param {string} value
   */
  function onOrderTypeCheckChange(value) {
    const itemOrderType = state.itemOrderType === value ? '' : value;
    setState({
      ...state,
      itemOrderType,
    });
  }

  function onToggleDetails() {
    setShowDetails(!showDetails);
  }

  function onGoToAttributes() {
    let path;
    if (isOnBoarding) {
      path = ACCOUNT_SETUP_PATH;
    } else {
      path = ITEM_PATH;
      if (id) {
        path = ITEM_EDIT_PATH.replace(':id', id);
      }
    }
    history.push(`${path}#attributes`);
  }

  useEffect(() => {
    setUnitOption(currentQuantityUnit);
  }, [currentQuantityUnit]);

  useEffect(() => {
    if (warehouseId && warehouseOptions) {
      const warehouse = [...warehouseOptions].find(function ({ value }) {
        return value === warehouseId;
      });
      if (warehouse) {
        onWarehouseChange(warehouse);
      }
    }
  }, [warehouseId, warehouseOptions]);

  useEffect(() => {
    if (itemCategory && categoryOptions) {
      const category = [...categoryOptions].find(function ({ value }) {
        return value === itemCategory;
      });
      if (category) {
        onCategoryChange(category);
      }
    }
  }, [itemCategory, categoryOptions]);

  return (
    <>
      <StyledCaption>Add Inventory Item</StyledCaption>
      <FlexRow marginBottom={15}>
        <FlexCol flexWidth={4}>
          <FlexRow>
            <FlexCol flexWidth={12}>
              <StyledSubHeading>Item Type</StyledSubHeading>
              {errors.itemType ? (
                <StyledErrorLabel>{errors.itemType}</StyledErrorLabel>
              ) : null}
            </FlexCol>
          </FlexRow>
          <FlexRow>
            <FlexCol flexWidth={12}>
              <CheckBox
                caption="Durable"
                inputId="durable"
                isChecked={state.itemType === DURABLE}
                description="Electronics, Furniture, Tools etc"
                onChange={onItemTypeCheckChange.bind(null, DURABLE)}
              />
            </FlexCol>
          </FlexRow>
          <FlexRow>
            <FlexCol flexWidth={12}>
              <CheckBox
                caption="Perishable"
                inputId="perishable"
                isChecked={isPerishable}
                onChange={onItemTypeCheckChange.bind(null, PERISHABLE)}
                description="Items with limited shelf life and an expiry date"
              />
            </FlexCol>
          </FlexRow>
          <FlexRow>
            <FlexCol flexWidth={12}>
              <StyledSubHeading>Order Type</StyledSubHeading>
              {errors.itemOrderType ? (
                <StyledErrorLabel>{errors.itemOrderType}</StyledErrorLabel>
              ) : null}
            </FlexCol>
          </FlexRow>
          <FlexRow>
            <FlexCol flexWidth={12}>
              <CheckBox
                inputId="for-ale"
                caption="For Sale"
                isChecked={state.itemOrderType === SALE}
                onChange={onOrderTypeCheckChange.bind(null, SALE)}
                description="Items that you sale to your customers"
              />
            </FlexCol>
          </FlexRow>
          <FlexRow>
            <FlexCol flexWidth={12}>
              <CheckBox
                caption="For Lease"
                inputId="for-lease"
                isChecked={isForLease}
                onChange={onOrderTypeCheckChange.bind(null, LEASE)}
                description="Move items from storage to project site and back"
              />
            </FlexCol>
          </FlexRow>
          <FlexRow>
            <FlexCol flexWidth={7}>
              <PhotoInput
                id="itemLogo"
                imageUri={itemLogo}
                dataTestId="itemLogo"
                caption="Item Picture"
                onChange={setItemLogo}
                buttonText="Choose From Device"
              />
            </FlexCol>
          </FlexRow>
        </FlexCol>
        <FlexCol flexWidth={4}>
          <FlexRow>
            <FlexCol flexWidth={12}>
              <TextInput
                isRequired
                name="name"
                inputId="name"
                caption="Name"
                inputType="text"
                value={state.name}
                disabled={isReadOnly}
                onChange={onInputChange}
                placeholder="Enter name"
                errorMessage={errors.name}
              />
            </FlexCol>
          </FlexRow>
          <FlexRow>
            <FlexCol flexWidth={12}>
              <SelectInput
                isRequired
                name="warehouse"
                isLoading={false}
                isClearable={true}
                caption="Warehouse"
                isSearchable={true}
                disabled={isReadOnly}
                value={warehouseOption}
                ariaLabelledBy="warehouse"
                options={warehouseOptions}
                onChange={onWarehouseChange}
                placeholder="Select warehouse"
                errorMessage={errors.warehouse}
                menuPortalTarget={window.document.body}
                description="Where will this be item be stored"
              />
            </FlexCol>
          </FlexRow>
          <FlexRow>
            <QuantityWrapper>
              <TextInput
                isRequired
                name="quantity"
                inputId="quantity"
                caption="Quantity"
                inputType="number"
                disabled={hasItemId}
                value={state.quantity}
                onChange={onInputChange}
                placeholder="Enter quantity"
                errorMessage={errors.quantity}
                footerMessage={
                  hasItemId ? `${ordering} Items available for ordering` : ''
                }
                description={`How many pieces do you have (max ${maxQuantity})`}
              />
              {!hasItemId ? null : (
                <QuantityButton onClick={onShowQuantityModal.bind(null, item)}>
                  Update Quantity
                </QuantityButton>
              )}
            </QuantityWrapper>
          </FlexRow>
          <FlexRow>
            <FlexCol flexWidth={12}>
              {isForLease ? (
                <TextInput
                  inputType="number"
                  name="leasingPrice"
                  disabled={isReadOnly}
                  inputId="leasingPrice"
                  caption="Leasing Price"
                  onChange={onInputChange}
                  value={state.leasingPrice}
                  placeholder="Enter leasing price"
                  errorMessage={errors.leasingPrice}
                  description="How much will you lease this item for"
                />
              ) : (
                <TextInput
                  name="sellPrice"
                  inputType="number"
                  inputId="sellPrice"
                  caption="Sell Price"
                  disabled={isReadOnly}
                  value={state.sellPrice}
                  onChange={onInputChange}
                  placeholder="Enter sell price"
                  errorMessage={errors.sellPrice}
                  description="How much will you sell this item for"
                />
              )}
            </FlexCol>
          </FlexRow>
          <FlexRow>
            {hasCondition ? null : (
              <FlexCol flexWidth={12}>
                <StyledButton
                  disabled={!id}
                  $link={hasItemId}
                  onClick={onGoToAttributes}
                  title={id ? 'Add attributes' : 'Save item to add attributes'}
                >
                  {hasItemId ? `${attributeCount} ` : 'Add '} Attributes
                  {hasItemId ? null : <AddIcon />}
                </StyledButton>
              </FlexCol>
            )}
          </FlexRow>
        </FlexCol>
        <FlexCol flexWidth={4}>
          <FlexRow>
            <FlexCol flexWidth={12}>
              <TextInput
                isRequired
                inputType="number"
                name="purchasePrice"
                disabled={isReadOnly}
                inputId="purchasePrice"
                caption="Purchase Price"
                onChange={onInputChange}
                value={state.purchasePrice}
                placeholder="Enter purchase price"
                errorMessage={errors.purchasePrice}
                description="How much did you buy this item. Important for Profit report"
              />
            </FlexCol>
          </FlexRow>
          <FlexRow>
            <FlexCol flexWidth={12}>
              {isPerishable ? (
                <DatePicker
                  isRequired
                  name="expiryDate"
                  inputId="expiryDate"
                  caption="Expiry Date"
                  disabled={isReadOnly}
                  selected={state.expiryDate}
                  placeholder="Select expiry date"
                  errorMessage={errors.expiryDate}
                  description="When does this item expire"
                  onChange={onDateChange.bind(null, 'expiryDate')}
                />
              ) : null}
            </FlexCol>
          </FlexRow>
          <FlexRow>
            <FlexCol flexWidth={12}>
              <SelectInput
                isRequired
                name="category"
                isLoading={false}
                caption="Category"
                isClearable={true}
                isSearchable={true}
                disabled={isReadOnly}
                value={categoryOption}
                ariaLabelledBy="category"
                options={categoryOptions}
                onChange={onCategoryChange}
                placeholder="Select category"
                errorMessage={errors.category}
                menuPortalTarget={window.document.body}
              />
            </FlexCol>
          </FlexRow>
          <FlexRow>
            <FlexCol flexWidth={12}>
              <SelectInput
                isRequired
                name="units"
                isLoading={false}
                isClearable={true}
                value={unitOption}
                isSearchable={true}
                disabled={isReadOnly}
                ariaLabelledBy="units"
                options={unitsOptions}
                caption="Quantity Unit"
                placeholder="Select unit"
                onChange={onQuantityUnitChange}
                errorMessage={errors.quantityUnit}
                menuPortalTarget={window.document.body}
                description="Choose the Unit of Measure for the item"
              />
            </FlexCol>
          </FlexRow>
          <FlexRow>
            <FlexCol flexWidth={12}>
              <StyledButton onClick={onToggleDetails}>
                {showDetails ? 'Hide' : 'Add'} More Details
                {showDetails ? <MinusIcon /> : <AddIcon />}
              </StyledButton>
            </FlexCol>
          </FlexRow>
        </FlexCol>
      </FlexRow>
    </>
  );
}

export default General;
